import { EApiLanguageCode, EApiTest } from '@datatype/iyagigo/api-type';
/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import * as size from '@constant/size';
import * as color from '@constant/color';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import TextBlock from '@component/common/block/text-block';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import { EStudyConfigurationMode, TIyagigoSubMenu, TTest } from '@datatype/iyagigo/type';
import { actions as modalActions } from '@store/slice/modal-slice';
import WorkbookTemplate from '@component/iyagigo/common/workbook-template';
import AssignmentControl from '@component/iyagigo/common/workbook-template/assignment-control';
import PieProgress from '@component/iyagigo/common/workbook-template/pie-progress';
import Gap from '@component/common/gap';
import Blur from '@component/common/blur';
import { useTodayTestWorkbookQuery } from '@hook/iyagigo/useApi';
import { useStudyConfiguration } from '@hook/iyagigo/useStudent';
import { getIsTodayStudied, getTestCodeFromCurrentStudy } from '@util/workbook';
import { actions as stundentActions } from '@store/slice/student-slice';
import IconBlock from '@component/common/block/icon-block';
import { useAuthPage } from '@hook/auth/useAuth';
import otterTestImage from '@asset/image/otter-test.webp';
import ImageBlock from '@component/common/block/image-block';
import IconTextBlock from '@component/common/block/icon-text-block';

// ############### variable ###############
// const BUTTON_WIDTH = IS_IYAGIGO_MOBILE_VIEW ? '100%' : '200px';
const BUTTON_WIDTH = '100%';

const TestDescriptionContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${size.MARGIN_1};
  padding: ${size.MARGIN_1};
  margin: ${size.MARGIN_1} 0px 0px 0px;
  background-color: ${color.BROWN_4};
  width: 100%;
`;

const BEGIN_BUTTON_DESIGN = {
  text: { size: 'medium', color: color.WHITE_1 },
  box: {
    padding: '5px 0px',
    borderRadius: size.BORDER_RADIUS_2,
    backgroundColor: color.BROWN_1,
    width: BUTTON_WIDTH,
    event: { cursor: 'pointer' },
  },
} as const;

const TodayStatusContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  gap: ${size.MARGIN_2};
  width: ${BUTTON_WIDTH};
`;

const BannerContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: ${size.MARGIN_2};
  width: 100%;
`;

const BannerContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const getTestName = (test: TTest) => {
  switch (test) {
    case EApiTest.TOEIC950:
      return '900점';
    case EApiTest.TOEIC850:
      return '800점';
    case EApiTest.TOEIC750:
      return '700점';
    case EApiTest.TOEIC650:
      return '600점';
    case EApiTest.TOEIC550:
      return '500점';
    case EApiTest.JLPT1:
      return 'N1';
    case EApiTest.JLPT2:
      return 'N2';
    case EApiTest.JLPT3:
      return 'N3';
    case EApiTest.JLPT4:
      return 'N4';
    case EApiTest.JLPT5:
      return 'N5';
    default:
      return '';
  }
};

const CONTENT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
  box: { alignX: 'left', margin: `0px 0px 0px 0px` },
} as const;

const TEST_DESCRIPTION_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
  box: { alignX: 'left' },
} as const;

const TestHome: React.FC<any> = () => {
  // ############### state ###############
  const dispatch = useTypedDispatch();
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const currentToeicTest = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentToeicTest) ?? null;
  const currentJlptTest = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentJlptTest) ?? null;
  const currentTest = studyLanguage === EApiLanguageCode.ENGLISH ? currentToeicTest : currentJlptTest;

  // ############### hook ###############
  const { openAuthPage } = useAuthPage();

  // ############### api ###############
  const workbookQuery = useTodayTestWorkbookQuery(getTestCodeFromCurrentStudy(currentTest));
  const studyConfiguration = useStudyConfiguration();

  // ############### variable ###############

  const isProgress = workbookQuery.data?.data?.some((item) => getIsTodayStudied(item.todayStudiedAt ?? ''));
  const isComplete = workbookQuery.data?.data?.every((item) => getIsTodayStudied(item.todayStudiedAt ?? ''));
  const studiedWorkbookCount =
    workbookQuery.data?.data?.filter((item) => getIsTodayStudied(item.todayStudiedAt ?? '')).length || 0;
  let totalWorkbookCount: number;

  switch (currentTest) {
    case EApiTest.TOEIC950:
      totalWorkbookCount = studyConfiguration?.toeic950TestGoal || 0;
      break;
    case EApiTest.TOEIC850:
      totalWorkbookCount = studyConfiguration?.toeic850TestGoal || 0;
      break;
    case EApiTest.TOEIC750:
      totalWorkbookCount = studyConfiguration?.toeic750TestGoal || 0;
      break;
    case EApiTest.TOEIC650:
      totalWorkbookCount = studyConfiguration?.toeic650TestGoal || 0;
      break;
    case EApiTest.TOEIC550:
      totalWorkbookCount = studyConfiguration?.toeic550TestGoal || 0;
      break;
    case EApiTest.JLPT1:
      totalWorkbookCount = studyConfiguration?.jlpt1TestGoal || 0;
      break;
    case EApiTest.JLPT2:
      totalWorkbookCount = studyConfiguration?.jlpt2TestGoal || 0;
      break;
    case EApiTest.JLPT3:
      totalWorkbookCount = studyConfiguration?.jlpt3TestGoal || 0;
      break;
    case EApiTest.JLPT4:
      totalWorkbookCount = studyConfiguration?.jlpt4TestGoal || 0;
      break;
    case EApiTest.JLPT5:
      totalWorkbookCount = studyConfiguration?.jlpt5TestGoal || 0;
      break;
    default:
      totalWorkbookCount = 0;
  }

  let TestDescription;

  if (currentTest?.startsWith('TOEIC')) {
    switch (currentTest) {
      case EApiTest.TOEIC550:
        TestDescription = (
          <TextBlock
            text={`<strong style="color: ${color.HIGHLIGHT_TEXT_COLOR};">500점대</strong>에서는 기초적인 영어 표현을 이해하고 단순한 문장을 읽을 수 있어요`}
            design={TEST_DESCRIPTION_DESIGN}
          />
        );
        break;
      case EApiTest.TOEIC650:
        TestDescription = (
          <TextBlock
            text={`<strong style="color: ${color.HIGHLIGHT_TEXT_COLOR};">600점대</strong>에서는 일상적인 주제에 대한 간단한 대화를 이해할 수 있어요`}
            design={TEST_DESCRIPTION_DESIGN}
          />
        );
        break;
      case EApiTest.TOEIC750:
        TestDescription = (
          <TextBlock
            text={`<strong style="color: ${color.HIGHLIGHT_TEXT_COLOR};">700점대</strong>에서는 다양한 주제로 의사소통이 가능하고 긴 글을 이해할 수 있어요`}
            design={TEST_DESCRIPTION_DESIGN}
          />
        );
        break;
      case EApiTest.TOEIC850:
        TestDescription = (
          <TextBlock
            text={`<strong style="color: ${color.HIGHLIGHT_TEXT_COLOR};">800점대</strong>에서는 비교적 복잡한 내용도 이해하고 표현할 수 있어요`}
            design={TEST_DESCRIPTION_DESIGN}
          />
        );
        break;
      case EApiTest.TOEIC950:
        TestDescription = (
          <TextBlock
            text={`<strong style="color: ${color.HIGHLIGHT_TEXT_COLOR};">900점대</strong>에서는 폭넓은 주제에 대해 정확하게 이해하고 소통할 수 있어요`}
            design={TEST_DESCRIPTION_DESIGN}
          />
        );
        break;
    }
  } else if (currentTest?.startsWith('JLPT')) {
    switch (currentTest) {
      case EApiTest.JLPT5:
        TestDescription = (
          <TextBlock
            text={`<strong style="color: ${color.HIGHLIGHT_TEXT_COLOR};">N5시험</strong>에서는 히라가나/가타카나, 어휘 800개 기본 한자 100자, 간단한 인사와 회화를 다뤄요`}
            design={TEST_DESCRIPTION_DESIGN}
          />
        );
        break;
      case EApiTest.JLPT4:
        TestDescription = (
          <TextBlock
            text={`<strong style="color: ${color.HIGHLIGHT_TEXT_COLOR};">N4시험</strong>에서는 어휘 1500개, 한자 300자, 기본 일상 대화를 다뤄요`}
            design={TEST_DESCRIPTION_DESIGN}
          />
        );
        break;
      case EApiTest.JLPT3:
        TestDescription = (
          <TextBlock
            text={`<strong style="color: ${color.HIGHLIGHT_TEXT_COLOR};">N3시험</strong>에서는 어휘 3800개 한자 600자, 일상생활 내용을 다뤄요`}
            design={TEST_DESCRIPTION_DESIGN}
          />
        );
        break;
      case EApiTest.JLPT2:
        TestDescription = (
          <TextBlock
            text={`<strong style="color: ${color.HIGHLIGHT_TEXT_COLOR};">N2시험</strong>에서는 어휘 6000개, 한자 1000자, 신문·평론 읽기를 다뤄요`}
            design={TEST_DESCRIPTION_DESIGN}
          />
        );
        break;
      case EApiTest.JLPT1:
        TestDescription = (
          <TextBlock
            text={`<strong style="color: ${color.HIGHLIGHT_TEXT_COLOR};">N1시험</strong>에서는 어휘 10000개, 한자 2000자, 전문적인 내용을 다뤄요`}
            design={TEST_DESCRIPTION_DESIGN}
          />
        );
        break;
    }
  } else {
    throw new Error('invalid test type');
  }

  // ############### handler ###############
  const handleTabClick = (test: TTest) => {
    // dispatch(stundentActions.storeCurrentTest(test));
  };

  const handleStudyButtonClick = () => {
    if (isComplete) return;
    dispatch(modalActions.showDialogModal({ type: 'today-test', data: { test: currentTest } }));
  };

  const handleGoSigninClick = () => {
    dispatch(modalActions.hideOverlay());
    openAuthPage('signin');
  };

  const handleTestSelect = () => {
    dispatch(
      modalActions.showDialogModal({
        type: 'study-configuration',
        data: {
          mode:
            studyLanguage === EApiLanguageCode.ENGLISH
              ? EStudyConfigurationMode.STUDY_ENGLISH_TEST_LEVEL
              : EStudyConfigurationMode.STUDY_JAPANESE_TEST_LEVEL,
        },
      }),
    );
  };

  return (
    <WorkbookTemplate selectedTabId={currentTest!} onTabClick={(id) => handleTabClick(id as TTest)}>
      <TextBlock
        text={`오늘의 ${studyLanguage === EApiLanguageCode.ENGLISH ? 'TOEIC' : 'JLPT'} 학습`}
        design={{
          text: { size: 'medium', color: color.BLACK_2, bold: true },
          box: { alignX: 'left' },
        }}
      />
      <Gap height={size.MARGIN_2} />
      <BannerContainer>
        <ImageBlock src={otterTestImage} alt='otter' design={{ image: { height: '80px' } }} />
        <BannerContent>
          <TextBlock text='현재 시험' design={{ text: { size: 'medium', color: color.BLACK_2 } }} />
          <TextBlock
            text={getTestName(currentTest)}
            design={{
              text: { size: 'large', color: color.BLACK_2, bold: true },
            }}
          />
          <IconTextBlock
            icon={'edit'}
            text='시험바꾸기'
            arrangement='text-icon'
            design={{
              text: { size: 'tiny', color: color.GRAY_1, underline: false },
              icon: { size: 'tiny', color: color.GRAY_1, kind: 'filled' },
              box: { event: { cursor: 'pointer' }, gap: '2px' },
            }}
            click={{ handler: handleTestSelect }}
          />
        </BannerContent>
      </BannerContainer>
      <Gap height={size.MARGIN_1} />
      <TestDescriptionContainer>
        <TextBlock
          text={`<strong style="color: ${color.HIGHLIGHT_TEXT_COLOR};">매일 10분,</strong> 꾸준히 ${studyLanguage === EApiLanguageCode.ENGLISH ? 'TOEIC을' : 'JLPT를'} 준비하며 읽기와 듣기 실력을 키울 수 있어요`}
          design={CONTENT_DESIGN}
        />
        {TestDescription}
      </TestDescriptionContainer>
      {!isAuthenticated && (
        <TextBlock
          text={'로그인하고 더 많은 어휘를 공부해보세요'}
          design={{
            text: { size: 'tiny', color: color.GRAY_1, bold: false, underline: true },
            box: { alignX: 'left', margin: `${size.MARGIN_1} 0 0 0`, event: { cursor: 'pointer' } },
          }}
          click={{ handler: handleGoSigninClick }}
        />
      )}
      <Gap height={size.MARGIN_2} />
      <Blur
        // text={`로그인하고\n학습설정을 해보세요`}
        icon={<IconBlock icon='lock' design={{ icon: { size: 'medium', color: color.GRAY_1 } }} />}
        blur={!isAuthenticated}>
        <TodayStatusContainer>
          <AssignmentControl
            type={
              studyLanguage === EApiLanguageCode.ENGLISH
                ? EStudyConfigurationMode.STUDY_ENGLISH_TEST_COUNT
                : EStudyConfigurationMode.STUDY_JAPANESE_TEST_COUNT
            }
            goal={totalWorkbookCount}
            suffix={'회'}
            isEditable={false}
          />
          <PieProgress studiedCount={studiedWorkbookCount} totalCount={totalWorkbookCount} />
        </TodayStatusContainer>
      </Blur>
      <Gap height={size.MARGIN_1} />
      <TextBlock
        text={isComplete ? '학습완료' : isProgress ? '다시보기' : '학습하기'}
        design={{
          ...BEGIN_BUTTON_DESIGN,
          box: {
            ...BEGIN_BUTTON_DESIGN.box,
            // backgroundColor: isComplete ? color.GRAY_DISABLE_BUTTON_COLOR : color.BROWN_1,
            backgroundColor: color.BROWN_1,
          },
        }}
        click={{ handler: handleStudyButtonClick }}
      />
    </WorkbookTemplate>
  );
};

export default TestHome;
